// SPDX-License-Identifier: GPL-3.0-only
// Copyright (C) 2026 Kyle Johnson

import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { BUILD_STAMP } from '../../lib/appVersion.js';
import { usePriceSync } from '../../hooks/usePriceSync.js';
import { useApp } from '../../store/AppContext.js';
import { useSettings } from '../../hooks/useSettings.js';
import { LanguageSwitcher } from './LanguageSwitcher.js';
import {
  deleteAccount,
  getKeyBundle,
  changePassword,
  fetchSyncLog,
  pushBlob,
  decodeBlob,
  decryptBlobs,
  rekeyLog,
} from '../../lib/syncClient.js';
import { clearCache } from '../../lib/indexedDb.js';
import {
  deriveAuthTokenViaWorker,
  unwrapDekViaWorker,
  wrapDekViaWorker,
  decryptStringViaWorker,
  generateDekViaWorker,
  encryptEventsViaWorker,
  buildKeyMaterialBundle,
  ARGON2_DEFAULT_PARAMS,
} from '../../store/useCryptoWorker.js';
import { evaluatePassword } from '../../lib/passwordStrength.js';
import { parseGnuCashFile, generateGnuCashFile } from '../../lib/gnucashXml.js';
import { buildEvent } from '../../lib/eventLog.js';
import { useEncryptAndPush } from '../../hooks/useEncryptAndPush.js';
import { CURRENCIES } from '../../lib/currencies.js';
import { formatDate } from '../../lib/formatLocale.js';
import * as bip39 from 'bip39';
import type { Account, LedgerEvent, SettingsKey, SettingsValues } from '../../types.js';
import { fetchCoinbaseAccounts } from '../../services/coinbase-api.js';
import { IS_NATIVE } from '../../native/platform.js';
import {
  getBiometryInfo,
  isBiometricEnabled,
  setBiometricEnabled,
  runBiometricPrompt,
} from '../../native/biometric.js';

interface SettingsProps {
  worker: Worker;
}

// ─── Shared style tokens ──────────────────────────────────────────────────────
const inputCls = 'w-full bg-bg border border-border text-text text-sm px-3 py-2 focus:border-accent focus:outline-none';
const selectCls = 'bg-bg border border-border text-text text-sm px-3 py-2 focus:border-accent focus:outline-none';
const labelCls = 'block text-xs text-text-muted mb-1';
const sectionCls = 'bg-bg-surface border border-border p-5';
const backBtnCls = 'text-accent hover:underline text-sm mb-4 flex items-center gap-1';
const primaryBtnCls = 'px-4 py-2 bg-accent text-white text-sm font-medium hover:bg-accent-hover disabled:opacity-50 transition-colors';
const secondaryBtnCls = 'px-4 py-2 border border-border text-sm text-text-muted hover:bg-bg-elevated disabled:opacity-50 transition-colors';
const rowCls = 'flex items-center justify-between py-4 border-b border-border last:border-b-0';
const rowLabelCls = 'text-sm text-text';

// ─── Toggle switch ────────────────────────────────────────────────────────────
function Toggle({ checked, onChange }: { checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-5 w-9 items-center transition-colors ${
        checked ? 'bg-accent' : 'bg-bg border border-border'
      }`}
    >
      <span
        className={`inline-block h-3 w-3 bg-white transition-transform ${
          checked ? 'translate-x-5' : 'translate-x-1'
        }`}
      />
    </button>
  );
}

// ─── Syncing badge ────────────────────────────────────────────────────────────
function SyncingBadge({ visible }: { visible: boolean }) {
  const { t } = useTranslation('common');
  if (!visible) return null;
  return <span className="text-xs text-text-muted ml-2 animate-pulse">{t('sync.syncing')}</span>;
}

// ─── Mobile & biometric settings (Phase 2 + Phase 4) ─────────────────────────
// Rendered inside the main Settings screen. The biometric row only appears on a
// native device with enrolled biometrics; the quick-entry default account is a
// synced setting useful anywhere. Uses the shared style tokens for consistency.
function MobileAppSection({
  settings,
  saveSetting,
  savingKey,
  accounts,
}: {
  settings: SettingsValues;
  saveSetting: <K extends SettingsKey>(key: K, value: SettingsValues[K]) => Promise<void>;
  savingKey: SettingsKey | null;
  accounts: Account[];
}) {
  const { t } = useTranslation(['settings', 'common', 'auth']);
  const [bioAvailable, setBioAvailable] = useState(false);
  const [bioName, setBioName] = useState(() => t('mobile.biometricUnlockDefault'));
  const [bioEnabled, setBioEnabled] = useState(false);
  const [bioBusy, setBioBusy] = useState(false);

  useEffect(() => {
    void (async () => {
      const info = await getBiometryInfo();
      setBioAvailable(info.available);
      setBioName(info.friendlyName);
      setBioEnabled(await isBiometricEnabled());
    })();
  }, []);

  async function toggleBiometric(v: boolean) {
    setBioBusy(true);
    try {
      if (v) {
        // Confirm biometry works before persisting the opt-in.
        const ok = await runBiometricPrompt(t('mobile.enablePrompt', { name: bioName }));
        if (!ok) return;
      }
      await setBiometricEnabled(v);
      setBioEnabled(v);
    } finally {
      setBioBusy(false);
    }
  }

  // Cash/bank/asset accounts make sensible quick-entry sources.
  const spendable = accounts.filter(
    (a) => !a.placeholder && !a.hidden && (a.type === 'CASH' || a.type === 'BANK' || a.type === 'ASSET' || a.type === 'CREDIT_CARD'),
  );

  return (
    <div className={sectionCls}>
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-text">{t('sections.mobile')}</h3>
        <SyncingBadge visible={savingKey === 'defaultQuickEntryAccount'} />
      </div>

      <div className={rowCls}>
        <div>
          <span className={rowLabelCls}>{t('accounting.defaultQuickEntry')}</span>
          <p className="text-xs text-text-subtle mt-0.5">
            {t('mobile.quickEntryNote')}
          </p>
        </div>
        <select
          className={selectCls}
          value={settings.defaultQuickEntryAccount ?? ''}
          onChange={(e) => void saveSetting('defaultQuickEntryAccount', e.target.value || null)}
        >
          <option value="">{t('accounting.autoFirstCash')}</option>
          {spendable.map((a) => (
            <option key={a.id} value={a.id}>{a.name}</option>
          ))}
        </select>
      </div>

      {IS_NATIVE && bioAvailable && (
        <div className={rowCls}>
          <div className="pr-4">
            <span className={rowLabelCls}>{t('auth:biometric.unlockWith', { method: bioName })}</span>
            <p className="text-xs text-text-subtle mt-0.5">
              {t('mobile.biometricNote', { name: bioName })}
            </p>
          </div>
          <Toggle checked={bioEnabled} onChange={(v) => { if (!bioBusy) void toggleBiometric(v); }} />
        </div>
      )}
    </div>
  );
}

export function Settings({ worker }: SettingsProps) {
  const { t } = useTranslation(['settings', 'common', 'errors', 'auth']);
  const { state, dispatch } = useApp();
  const { auth, ledger } = state;
  const navigate = useNavigate();
  const encryptAndPush = useEncryptAndPush(worker, auth.keyData);
  const { settings, updateSetting } = useSettings(worker);
  const priceSync = usePriceSync(worker);

  // Track which key is currently saving
  const [savingKey, setSavingKey] = useState<SettingsKey | null>(null);

  const saveSetting = useCallback(
    async <K extends SettingsKey>(key: K, value: SettingsValues[K]) => {
      setSavingKey(key);
      try {
        await updateSetting(key, value);
      } finally {
        setSavingKey(null);
      }
    },
    [updateSetting],
  );

  // ── Section navigation ──────────────────────────────────────────────────────
  const [section, setSection] = useState<
    'main' | 'change-password' | 'delete' | 'import' | 'rotate-key'
  >('main');

  // ── Currency picker state ───────────────────────────────────────────────────
  const [currencySearch, setCurrencySearch] = useState('');
  const [showCurrencyPicker, setShowCurrencyPicker] = useState(false);
  const [homeCurrencyInput, setHomeCurrencyInput] = useState(settings.homeCurrency);
  const [savingCurrency, setSavingCurrency] = useState(false);
  const [currencyWarning, setCurrencyWarning] = useState('');

  const filteredCurrencies = CURRENCIES.filter(
    (c) =>
      c.code.toLowerCase().includes(currencySearch.toLowerCase()) ||
      c.name.toLowerCase().includes(currencySearch.toLowerCase()),
  ).slice(0, 50);

  async function handleSaveHomeCurrency() {
    if (homeCurrencyInput === settings.homeCurrency) {
      setShowCurrencyPicker(false);
      return;
    }
    setSavingCurrency(true);
    try {
      await updateSetting('homeCurrency', homeCurrencyInput);
      setShowCurrencyPicker(false);
      setCurrencyWarning('');
    } catch (err) {
      setCurrencyWarning(err instanceof Error ? err.message : t('validation.saveCurrencyFailed'));
    } finally {
      setSavingCurrency(false);
    }
  }

  // ── Password change ─────────────────────────────────────────────────────────
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNew, setConfirmNew] = useState('');
  const [changeError, setChangeError] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);
  const [changeStep, setChangeStep] = useState('');

  async function handleChangePassword(e: React.FormEvent) {
    e.preventDefault();
    setChangeError('');
    if (!oldPassword) { setChangeError(t('validation.currentPasswordRequired')); return; }
    const strength = evaluatePassword(newPassword);
    if (!strength.ok) { setChangeError(t(`auth:passwordStrength.${strength.messageKey}`, strength.messageParams ?? {})); return; }
    if (newPassword !== confirmNew) { setChangeError(t('validation.newPasswordsMismatch')); return; }

    setChangingPassword(true);
    try {
      const username = auth.username ?? '';
      const config = { baseUrl: auth.syncUrl, token: auth.token ?? '' };
      setChangeStep(t('auth:login.deriving'));
      const oldServerAuthToken = await deriveAuthTokenViaWorker(worker, oldPassword, username);
      const newServerAuthToken = await deriveAuthTokenViaWorker(worker, newPassword, username);

      setChangeStep(t('auth:login.fetchingKeys'));
      const bundle = await getKeyBundle(config);

      setChangeStep(t('security.verifyingCurrent'));
      // Unwrapping the DEK with the old password both proves the password is
      // correct and yields the DEK to re-wrap. GCM auth fails → wrong password.
      let dek: ArrayBuffer;
      try {
        dek = await unwrapDekViaWorker(worker, bundle.wrappedByPassword, oldPassword, bundle.kdfParams);
      } catch {
        setChangeError(t('validation.currentPasswordIncorrect'));
        return;
      }

      setChangeStep(t('security.rewrapping'));
      // Only the password wrap changes; the mnemonic wrap and self-blob are the
      // same DEK and stay as-is (a password change never re-keys the ledger).
      const newWrapSalt = crypto.getRandomValues(new Uint8Array(32));
      const wrapSaltHex = Array.from(newWrapSalt).map((b) => b.toString(16).padStart(2, '0')).join('');
      const rewrapped = await wrapDekViaWorker(worker, dek, newPassword, newWrapSalt);
      const newBundle = {
        ...bundle,
        kdfParams: ARGON2_DEFAULT_PARAMS,
        wrappedByPassword: { wrapSalt: wrapSaltHex, iv: rewrapped.iv, ciphertext: rewrapped.ciphertext },
      };

      setChangeStep(t('security.updatingServer'));
      await changePassword(config, oldServerAuthToken, newServerAuthToken, newBundle);
      setSection('main');
      setOldPassword(''); setNewPassword(''); setConfirmNew('');
    } catch (err) {
      setChangeError(err instanceof Error ? err.message : t('validation.changePasswordFailed'));
    } finally {
      setChangingPassword(false);
      setChangeStep('');
    }
  }

  // ── Rotate encryption key (SEC-07) ───────────────────────────────────────────
  // Generates a NEW data-encryption key, re-encrypts the entire ledger under it,
  // and atomically replaces the server log + key material. Requires the password
  // (to re-wrap) and the recovery phrase (the DEK is also wrapped under it).
  const [rotatePassword, setRotatePassword] = useState('');
  const [rotateMnemonic, setRotateMnemonic] = useState('');
  const [rotateError, setRotateError] = useState('');
  const [rotating, setRotating] = useState(false);
  const [rotateStep, setRotateStep] = useState('');

  async function handleRotateKey(e: React.FormEvent) {
    e.preventDefault();
    setRotateError('');
    if (!rotatePassword) { setRotateError(t('validation.passwordRequired')); return; }
    const trimmed = rotateMnemonic.trim().toLowerCase().replace(/\s+/g, ' ');
    if (!bip39.validateMnemonic(trimmed)) {
      setRotateError(t('validation.recoveryPhraseForRotation'));
      return;
    }
    if (!auth.keyData) { setRotateError(t('security.sessionKeyUnavailable')); return; }

    setRotating(true);
    try {
      const config = { baseUrl: auth.syncUrl, token: auth.token ?? '' };
      const entropyHex = bip39.mnemonicToEntropy(trimmed);
      const entropy = new Uint8Array(entropyHex.length / 2);
      for (let i = 0; i < entropy.length; i++) entropy[i] = parseInt(entropyHex.slice(i * 2, i * 2 + 2), 16);

      setRotateStep(t('security.verifyingPassword'));
      const bundle = await getKeyBundle(config);
      try {
        await unwrapDekViaWorker(worker, bundle.wrappedByPassword, rotatePassword, bundle.kdfParams);
      } catch {
        setRotateError(t('validation.incorrectPassword'));
        return;
      }

      setRotateStep(t('security.fetchingDecrypting'));
      const blobs = await fetchSyncLog(config, 0);
      const events = await decryptBlobs(blobs, worker, auth.keyData);

      setRotateStep(t('security.generatingKey'));
      const newDek = await generateDekViaWorker(worker);

      setRotateStep(t('security.reencrypting'));
      const reEncrypted = await encryptEventsViaWorker(worker, events, newDek);
      const newBundle = await buildKeyMaterialBundle(worker, newDek, rotatePassword, entropy, trimmed);

      setRotateStep(t('security.uploading'));
      await rekeyLog(config, newBundle, [reEncrypted]);

      // Adopt the new DEK in-memory + storage; drop the now-stale encrypted cache.
      dispatch({ type: 'KEY_ROTATED', keyData: newDek });
      await clearCache();

      setRotatePassword(''); setRotateMnemonic('');
      setSection('main');
    } catch (err) {
      setRotateError(err instanceof Error ? err.message : t('validation.keyRotationFailed'));
    } finally {
      setRotating(false);
      setRotateStep('');
    }
  }

  // ── Delete account ──────────────────────────────────────────────────────────
  const [deleteConfirm, setDeleteConfirm] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  async function handleDeleteAccount() {
    if (deleteConfirm !== auth.username) {
      setDeleteError(t('validation.typeUsernameToConfirm'));
      return;
    }
    setDeleting(true);
    setDeleteError('');
    try {
      if (auth.token) await deleteAccount({ baseUrl: auth.syncUrl, token: auth.token });
      await clearCache();
      dispatch({ type: 'AUTH_LOGOUT' });
    } catch (err) {
      setDeleteError(err instanceof Error ? err.message : t('errors:deleteAccount'));
      setDeleting(false);
    }
  }

  // ── GnuCash import ──────────────────────────────────────────────────────────
  const importFileRef = useRef<HTMLInputElement>(null);
  const [importStep, setImportStep] = useState('');
  const [importing, setImporting] = useState(false);
  const [importError, setImportError] = useState('');
  const [importSuccess, setImportSuccess] = useState('');

  async function handleImport(e: React.FormEvent) {
    e.preventDefault();
    const file = importFileRef.current?.files?.[0];
    if (!file) { setImportError(t('validation.selectGnuCashFile')); return; }
    if (!auth.keyData) { setImportError(t('noEncryptionKey')); return; }
    setImporting(true); setImportError(''); setImportSuccess('');
    try {
      setImportStep(t('import.readingFile'));
      const buf = await file.arrayBuffer();
      setImportStep(t('import.parsing'));
      const events = await parseGnuCashFile(buf);
      setImportStep(t('import.encrypting', { count: events.length }));
      const encryptedBlob = await new Promise<Uint8Array>((resolve, reject) => {
        const id = `encrypt-import-${Date.now()}`;
        const handler = (ev: MessageEvent) => {
          const msg = ev.data as { type: string; id: string; ciphertext?: number[]; message?: string };
          if (msg.id !== id) return;
          worker.removeEventListener('message', handler);
          if (msg.type === 'ENCRYPT_RESULT' && msg.ciphertext) resolve(new Uint8Array(msg.ciphertext));
          else reject(new Error(msg.message ?? 'Encryption failed'));
        };
        worker.addEventListener('message', handler);
        worker.postMessage({ type: 'ENCRYPT', id, events, keyData: auth.keyData });
      });
      setImportStep(t('import.pushing'));
      const config = { baseUrl: auth.syncUrl, token: auth.token ?? '' };
      const result = await pushBlob(config, encryptedBlob);
      dispatch({ type: 'EVENTS_LOADED', events });
      setImportSuccess(
        t('import.success', {
          events: events.length,
          accounts: events.filter((ev) => ev.type === 'AccountCreated').length,
          transactions: events.filter((ev) => ev.type === 'TransactionPosted').length,
          sequence: result.sequence,
        }),
      );
      setSection('main');
    } catch (err) {
      setImportError(err instanceof Error ? err.message : t('import.failed'));
    } finally {
      setImporting(false); setImportStep('');
    }
  }

  // ── GnuCash export ──────────────────────────────────────────────────────────
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState('');

  async function handleExport() {
    if (!auth.keyData) { setExportError(t('noEncryptionKey')); return; }
    setExporting(true); setExportError('');
    try {
      const config = { baseUrl: auth.syncUrl, token: auth.token ?? '' };
      const blobs = await fetchSyncLog(config);
      const allEvents: LedgerEvent[] = [];
      for (const blob of blobs) {
        if (!blob.data) continue;
        const bytes = decodeBlob(blob.data);
        const events = await new Promise<LedgerEvent[]>((resolve, reject) => {
          const id = `decrypt-export-${blob.sequence}-${Date.now()}`;
          const handler = (ev: MessageEvent) => {
            const msg = ev.data as { type: string; id: string; events?: LedgerEvent[]; message?: string };
            if (msg.id !== id) return;
            worker.removeEventListener('message', handler);
            if (msg.type === 'DECRYPT_RESULT' && msg.events) resolve(msg.events);
            else reject(new Error(msg.message ?? 'Decryption failed'));
          };
          worker.addEventListener('message', handler);
          worker.postMessage({ type: 'DECRYPT', id, blob: Array.from(bytes), keyData: auth.keyData });
        });
        allEvents.push(...events);
      }
      allEvents.sort((a, b) => a.sequence - b.sequence);
      const compressed = await generateGnuCashFile(allEvents);
      const blobObj = new Blob([compressed.buffer as ArrayBuffer], { type: 'application/octet-stream' });
      const url = URL.createObjectURL(blobObj);
      const a = document.createElement('a');
      a.href = url; a.download = 'ledger.gnucash';
      document.body.appendChild(a); a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      setExportError(err instanceof Error ? err.message : t('data.exportFailed'));
    } finally {
      setExporting(false);
    }
  }

  // ── Reveal recovery phrase ────────────────────────────────────────────────────
  // Password-gated: fetch the encrypted mnemonic blob, derive the wrap key from
  // the entered password, and decrypt in the worker. A wrong password fails the
  // AES-GCM auth tag (throws) or yields a non-BIP39 string — both map to
  // "Incorrect password". The plaintext lives only in component state until Hide.
  const [recoverySection, setRecoverySection] = useState<'hidden' | 'prompt'>('hidden');
  const [recoveryPassword, setRecoveryPassword] = useState('');
  const [recoveryPhrase, setRecoveryPhrase] = useState<string | null>(null);
  const [recoveryError, setRecoveryError] = useState('');
  const [revealingRecovery, setRevealingRecovery] = useState(false);

  async function handleRevealRecovery(e: React.FormEvent) {
    e.preventDefault();
    setRecoveryError('');
    if (!recoveryPassword) { setRecoveryError(t('validation.passwordRequired')); return; }
    setRevealingRecovery(true);
    try {
      const config = { baseUrl: auth.syncUrl, token: auth.token ?? '' };
      const bundle = await getKeyBundle(config);
      if (!bundle.mnemonicSelfBlob) {
        setRecoveryError(t('validation.recoveryUnavailable'));
        return;
      }
      let mnemonic: string;
      try {
        // Re-enter password → unwrap the DEK (proves the password) → decrypt the
        // DEK-encrypted mnemonic copy. The server never held a crackable copy.
        const dek = await unwrapDekViaWorker(worker, bundle.wrappedByPassword, recoveryPassword, bundle.kdfParams);
        mnemonic = await decryptStringViaWorker(worker, bundle.mnemonicSelfBlob, dek);
      } catch {
        setRecoveryError(t('validation.incorrectPassword'));
        return;
      }
      if (!bip39.validateMnemonic(mnemonic)) {
        setRecoveryError(t('validation.incorrectPassword'));
        return;
      }
      setRecoveryPhrase(mnemonic);
      setRecoveryPassword('');
      setRecoverySection('hidden');
    } catch (err) {
      setRecoveryError(err instanceof Error ? err.message : t('validation.revealRecoveryFailed'));
    } finally {
      setRevealingRecovery(false);
    }
  }

  function resetRecovery() {
    setRecoveryPhrase(null);
    setRecoverySection('hidden');
    setRecoveryPassword('');
    setRecoveryError('');
  }

  // ── Coinbase ────────────────────────────────────────────────────────────────
  const [apiKeyInput, setApiKeyInput] = useState('');
  const [apiSecretInput, setApiSecretInput] = useState('');
  const [verifyingCoinbase, setVerifyingCoinbase] = useState(false);
  const [coinbaseError, setCoinbaseError] = useState('');
  const [disconnectingCoinbase, setDisconnectingCoinbase] = useState(false);

  async function handleSaveCoinbaseCredential(e: React.FormEvent) {
    e.preventDefault();
    let apiKey = apiKeyInput.trim();
    let apiSecret = apiSecretInput.trim();

    // Accept the full Coinbase JSON download file pasted into the private key field.
    // Extracts "name" → API key, "privateKey" → PEM secret, auto-filling the key name
    // field if the user left it empty.
    if (apiSecret.trimStart().startsWith('{')) {
      try {
        const parsed = JSON.parse(apiSecret) as { name?: string; privateKey?: string };
        if (parsed.privateKey) {
          if (!apiKey && typeof parsed.name === 'string') apiKey = parsed.name;
          apiSecret = parsed.privateKey;
        }
      } catch { /* not JSON — treat as raw PEM */ }
    }

    if (!apiKey || !apiSecret) {
      setCoinbaseError(t('validation.apiKeyAndSecretRequired'));
      return;
    }
    setVerifyingCoinbase(true);
    setCoinbaseError('');
    try {
      await fetchCoinbaseAccounts({ apiKey, apiSecret, jwtToken: auth.token ?? '' });
      await updateSetting('coinbaseCredential', {
        apiKey,
        apiSecret,
        connectedAt: new Date().toISOString(),
        accountIds: [],
        lastSyncedAt: null,
        lastSyncTransactionCount: 0,
      });
      setApiKeyInput('');
      setApiSecretInput('');
    } catch (err) {
      setCoinbaseError(
        err instanceof Error
          ? t('validation.couldNotVerifyCredentialsDetail', { message: err.message })
          : t('validation.couldNotVerifyCredentials'),
      );
    } finally {
      setVerifyingCoinbase(false);
    }
  }

  async function handleDisconnectCoinbase() {
    setDisconnectingCoinbase(true);
    try {
      const event = await buildEvent(
        'CredentialRevoked',
        { key: 'coinbaseCredential', reason: 'user_disconnected', revokedAt: new Date().toISOString() },
        ledger.lastSequence + 1,
      );
      await encryptAndPush(event);
    } catch (err) {
      setCoinbaseError(err instanceof Error ? err.message : t('coinbase.disconnectFailed'));
    } finally {
      setDisconnectingCoinbase(false);
    }
  }

  // ─── Section renders ────────────────────────────────────────────────────────

  if (section === 'import') {
    return (
      <div className="flex-1 overflow-auto p-6 max-w-md">
        <button onClick={() => { setSection('main'); setImportError(''); setImportSuccess(''); }} className={backBtnCls}>
          ← {t('common:actions.back')}
        </button>
        <h2 className="text-md font-semibold text-text mb-2">{t('security.importGnuCash')}</h2>
        <p className="text-sm text-text-muted mb-4">
          {t('import.selectFilePre')} <code className="bg-bg-elevated px-1 font-mono">.gnucash</code> {t('import.selectFilePost')}
        </p>
        {importError && <div className="border border-negative text-negative px-3 py-2 text-sm mb-4">{importError}</div>}
        <form onSubmit={handleImport} className="space-y-4">
          <div>
            <label className={labelCls}>{t('security.gnuCashFile')}</label>
            <input ref={importFileRef} type="file" accept=".gnucash"
              className="w-full text-sm text-text-muted file:mr-3 file:py-1.5 file:px-3 file:border-0 file:bg-bg-elevated file:text-text-muted hover:file:bg-bg-surface" />
          </div>
          <button type="submit" disabled={importing} className={`w-full py-2 ${primaryBtnCls}`}>
            {importing ? (importStep || t('import.importing')) : t('common:nav.import')}
          </button>
        </form>
      </div>
    );
  }

  if (section === 'change-password') {
    return (
      <div className="flex-1 overflow-auto p-6 max-w-md">
        <button onClick={() => setSection('main')} className={backBtnCls}>← {t('common:actions.back')}</button>
        <h2 className="text-md font-semibold text-text mb-6">{t('security.changePassword')}</h2>
        {changeError && <div className="border border-negative text-negative px-3 py-2 text-sm mb-4">{changeError}</div>}
        <form onSubmit={handleChangePassword} className="space-y-4">
          <div><label className={labelCls}>{t('security.currentPassword')}</label>
            <input type="password" value={oldPassword} onChange={(e) => setOldPassword(e.target.value)} className={inputCls} />
          </div>
          <div><label className={labelCls}>{t('security.newPassword')}</label>
            <input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} className={inputCls} />
          </div>
          <div><label className={labelCls}>{t('security.confirmNewPassword')}</label>
            <input type="password" value={confirmNew} onChange={(e) => setConfirmNew(e.target.value)} className={inputCls} />
          </div>
          <button type="submit" disabled={changingPassword} className={`w-full py-2 ${primaryBtnCls}`}>
            {changingPassword ? (changeStep || t('security.updating')) : t('security.updatePassword')}
          </button>
        </form>
      </div>
    );
  }

  if (section === 'rotate-key') {
    return (
      <div className="flex-1 overflow-auto p-6 max-w-md">
        <button onClick={() => setSection('main')} className={backBtnCls}>← {t('common:actions.back')}</button>
        <h2 className="text-md font-semibold text-text mb-2">{t('security.rotateKey')}</h2>
        <p className="text-text-muted text-sm mb-6">
          {t('security.rotateDesc')}
        </p>
        {rotateError && <div className="border border-negative text-negative px-3 py-2 text-sm mb-4">{rotateError}</div>}
        <form onSubmit={handleRotateKey} className="space-y-4">
          <div><label className={labelCls}>{t('security.currentPassword')}</label>
            <input type="password" value={rotatePassword} onChange={(e) => setRotatePassword(e.target.value)} className={inputCls} autoComplete="current-password" />
          </div>
          <div><label className={labelCls}>{t('security.recoveryPhrase')}</label>
            <textarea value={rotateMnemonic} onChange={(e) => setRotateMnemonic(e.target.value)} rows={3}
              className="w-full bg-bg border border-border text-text text-sm px-3 py-2 focus:border-accent focus:outline-none font-mono resize-none"
              placeholder={t('security.mnemonicPlaceholder')} autoComplete="off" autoCorrect="off" autoCapitalize="off" spellCheck={false} />
          </div>
          <button type="submit" disabled={rotating} className={`w-full py-2 ${primaryBtnCls}`}>
            {rotating ? (rotateStep || t('security.rotating')) : t('security.rotateKeyButton')}
          </button>
        </form>
      </div>
    );
  }

  if (section === 'delete') {
    return (
      <div className="flex-1 overflow-auto p-6 max-w-md">
        <button onClick={() => setSection('main')} className={backBtnCls}>← {t('common:actions.back')}</button>
        <h2 className="text-md font-semibold text-text mb-2">{t('security.deleteAccount')}</h2>
        <p className="text-text-muted text-sm mb-6">
          {t('deleteAccountDesc')}
        </p>
        {deleteError && <div className="border border-negative text-negative px-3 py-2 text-sm mb-4">{deleteError}</div>}
        <div className="space-y-4">
          <div>
            <label className={labelCls}>
              {t('deleteConfirmPre')} <span className="font-mono text-text">"{auth.username}"</span> {t('deleteConfirmPost')}
            </label>
            <input type="text" value={deleteConfirm} onChange={(e) => setDeleteConfirm(e.target.value)}
              className="w-full bg-bg border border-negative text-text text-sm px-3 py-2 focus:border-negative focus:outline-none" />
          </div>
          <button onClick={handleDeleteAccount} disabled={deleting || deleteConfirm !== auth.username}
            className="w-full py-2 border border-negative text-negative text-sm font-medium hover:bg-negative/10 disabled:opacity-50 transition-colors">
            {deleting ? t('security.deleting') : t('security.permanentlyDelete')}
          </button>
        </div>
      </div>
    );
  }

  // ── Main settings page ──────────────────────────────────────────────────────

  const coinbaseCredential = settings.coinbaseCredential;
  const lastSyncedDisplay = String(
    coinbaseCredential?.lastSyncedAt
      ? (() => {
          const diff = Math.round((Date.now() - new Date(coinbaseCredential.lastSyncedAt).getTime()) / 60000);
          return diff < 1 ? t('time.justNow') : diff === 1 ? t('time.minuteAgo') : t('time.minutesAgo', { count: diff });
        })()
      : t('time.never'),
  );
  const connected = coinbaseCredential !== null;

  return (
    <div className="flex-1 overflow-auto p-6 max-w-2xl">
      <h2 className="text-md font-semibold text-text mb-6">{t('title')}</h2>

      <div className="space-y-4">

        {/* ── Display ── */}
        <div className={sectionCls}>
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-text">{t('sections.display')}</h3>
            <SyncingBadge visible={
              savingKey === 'homeCurrency' || savingKey === 'dateFormat' ||
              savingKey === 'numberFormat' || savingKey === 'theme' ||
              savingKey === 'fontSize' || savingKey === 'registerRowHeight'
            } />
          </div>

          <div className={rowCls}>
            <span className={rowLabelCls}>{t('display.homeCurrency')}</span>
            {showCurrencyPicker ? (
              <div className="w-64 space-y-2">
                <input type="text" value={currencySearch} onChange={(e) => setCurrencySearch(e.target.value)}
                  className={inputCls} placeholder={t('display.searchCurrencies')} autoFocus />
                <select size={5} className="w-full bg-bg border border-border text-text text-sm px-2 py-1 focus:border-accent focus:outline-none"
                  value={homeCurrencyInput} onChange={(e) => setHomeCurrencyInput(e.target.value)}>
                  {filteredCurrencies.map((c) => (
                    <option key={c.code} value={c.code}>{c.symbol} {c.code} — {c.name}</option>
                  ))}
                </select>
                {currencyWarning && <div className="border border-negative text-negative px-3 py-2 text-sm">{currencyWarning}</div>}
                <div className="flex gap-2">
                  <button onClick={() => { setShowCurrencyPicker(false); setHomeCurrencyInput(settings.homeCurrency); setCurrencyWarning(''); }}
                    className="flex-1 py-1 border border-border text-sm text-text-muted hover:bg-bg-elevated transition-colors">{t('common:actions.cancel')}</button>
                  <button onClick={handleSaveHomeCurrency} disabled={savingCurrency}
                    className="flex-1 py-1 bg-accent text-white text-sm hover:bg-accent-hover disabled:opacity-40 transition-colors">
                    {savingCurrency ? t('saving') : t('common:actions.save')}
                  </button>
                </div>
              </div>
            ) : (
              <button onClick={() => { setShowCurrencyPicker(true); setHomeCurrencyInput(settings.homeCurrency); setCurrencySearch(''); }}
                className="text-sm text-text font-mono bg-bg border border-border px-3 py-1.5 hover:border-accent transition-colors">
                {settings.homeCurrency} — {CURRENCIES.find((c) => c.code === settings.homeCurrency)?.name ?? settings.homeCurrency}
              </button>
            )}
          </div>

          <div className={rowCls}>
            <span className={rowLabelCls}>{t('display.dateFormat')}</span>
            <select className={selectCls} value={settings.dateFormat}
              onChange={(e) => void saveSetting('dateFormat', e.target.value)}>
              <option value="MM/DD/YYYY">MM/DD/YYYY</option>
              <option value="DD/MM/YYYY">DD/MM/YYYY</option>
              <option value="YYYY-MM-DD">YYYY-MM-DD</option>
            </select>
          </div>

          <div className={rowCls}>
            <span className={rowLabelCls}>{t('display.numberFormat')}</span>
            <select className={selectCls} value={settings.numberFormat}
              onChange={(e) => void saveSetting('numberFormat', e.target.value as 'us' | 'eu')}>
              <option value="us">{t('display.numberUs')}</option>
              <option value="eu">{t('display.numberEu')}</option>
            </select>
          </div>

          <div className={rowCls}>
            <span className={rowLabelCls}>{t('display.language')}</span>
            <LanguageSwitcher worker={worker} className={selectCls} />
          </div>

          <div className={rowCls}>
            <span className={rowLabelCls}>{t('display.theme')}</span>
            <select className={selectCls} value={settings.theme}
              onChange={(e) => void saveSetting('theme', e.target.value as 'dark' | 'light' | 'system')}>
              <option value="dark">{t('display.themeDark')}</option>
              <option value="light">{t('display.themeLight')}</option>
              <option value="system">{t('display.themeSystem')}</option>
            </select>
          </div>

          <div className={rowCls}>
            <span className={rowLabelCls}>{t('display.fontSize')}</span>
            <select className={selectCls} value={settings.fontSize}
              onChange={(e) => void saveSetting('fontSize', e.target.value as 'sm' | 'md' | 'lg')}>
              <option value="sm">{t('display.fontSmall')}</option>
              <option value="md">{t('display.fontMedium')}</option>
              <option value="lg">{t('display.fontLarge')}</option>
            </select>
          </div>

          <div className={rowCls}>
            <span className={rowLabelCls}>{t('display.registerRowHeight')}</span>
            <select className={selectCls} value={settings.registerRowHeight}
              onChange={(e) => void saveSetting('registerRowHeight', e.target.value as 'compact' | 'normal' | 'comfortable')}>
              <option value="compact">{t('display.rowCompact')}</option>
              <option value="normal">{t('display.rowNormal')}</option>
              <option value="comfortable">{t('display.rowComfortable')}</option>
            </select>
          </div>
        </div>

        {/* ── Accounting ── */}
        <div className={sectionCls}>
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-text">{t('sections.accounting')}</h3>
            <SyncingBadge visible={
              savingKey === 'costBasisMethod' || savingKey === 'defaultCurrencyGainAccount' ||
              savingKey === 'defaultCommissionAccount' || savingKey === 'scheduledTxLookAheadDays' ||
              savingKey === 'reconciliationThreshold'
            } />
          </div>

          <div className={rowCls}>
            <div>
              <span className={rowLabelCls}>{t('accounting.costBasisMethod')}</span>
              <p className="text-xs text-text-subtle mt-0.5">
                {settings.costBasisMethod === 'fifo'
                  ? t('accounting.fifoNote')
                  : t('accounting.averageNote')}
              </p>
            </div>
            <select className={selectCls} value={settings.costBasisMethod}
              onChange={(e) => void saveSetting('costBasisMethod', e.target.value as 'fifo' | 'average')}>
              <option value="fifo">{t('accounting.fifoFull')}</option>
              <option value="average">{t('accounting.averageCost')}</option>
            </select>
          </div>

          <div className={rowCls}>
            <div>
              <span className={rowLabelCls}>{t('accounting.currencyGainAccount')}</span>
              <p className="text-xs text-text-subtle mt-0.5">{t('accounting.currencyGainNote')}</p>
            </div>
            <select className={selectCls} value={settings.defaultCurrencyGainAccount ?? ''}
              onChange={(e) => void saveSetting('defaultCurrencyGainAccount', e.target.value || null)}>
              <option value="">{t('selectAccount')}</option>
              {[...state.ledger.accounts.values()]
                .filter((a) => a.type === 'INCOME' && !a.hidden)
                .map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
          </div>

          <div className={rowCls}>
            <div>
              <span className={rowLabelCls}>{t('accounting.commissionAccount')}</span>
              <p className="text-xs text-text-subtle mt-0.5">{t('accounting.commissionNote')}</p>
            </div>
            <select className={selectCls} value={settings.defaultCommissionAccount ?? ''}
              onChange={(e) => void saveSetting('defaultCommissionAccount', e.target.value || null)}>
              <option value="">{t('selectAccount')}</option>
              {[...state.ledger.accounts.values()]
                .filter((a) => a.type === 'EXPENSE' && !a.hidden)
                .map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
          </div>

          <div className={rowCls}>
            <span className={rowLabelCls}>{t('accounting.scheduledLookAhead')}</span>
            <div className="flex items-center gap-2">
              <input type="number" min={0} max={365}
                className="w-20 bg-bg border border-border text-text text-sm px-3 py-2 focus:border-accent focus:outline-none"
                value={settings.scheduledTxLookAheadDays}
                onChange={(e) => void saveSetting('scheduledTxLookAheadDays', parseInt(e.target.value, 10) || 0)}
              />
              <span className="text-sm text-text-muted">{t('accounting.days')}</span>
            </div>
          </div>

          <div className={rowCls}>
            <span className={rowLabelCls}>{t('accounting.reconciliationWarning')}</span>
            <div className="flex items-center gap-2">
              <input type="number" min={0} max={365}
                className="w-20 bg-bg border border-border text-text text-sm px-3 py-2 focus:border-accent focus:outline-none"
                value={settings.reconciliationThreshold}
                onChange={(e) => void saveSetting('reconciliationThreshold', parseInt(e.target.value, 10) || 0)}
              />
              <span className="text-sm text-text-muted">{t('accounting.days')}</span>
            </div>
          </div>
        </div>

        {/* ── Mobile ── */}
        <MobileAppSection
          settings={settings}
          saveSetting={saveSetting}
          savingKey={savingKey}
          accounts={[...state.ledger.accounts.values()]}
        />

        {/* ── Register Display ── */}
        <div className={sectionCls}>
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-text">{t('sections.registerDisplay')}</h3>
            <SyncingBadge visible={
              savingKey === 'showRunningBalance' || savingKey === 'showReconcileColumn' ||
              savingKey === 'defaultSortOrder' || savingKey === 'transactionJournalMode'
            } />
          </div>

          <div className={rowCls}>
            <span className={rowLabelCls}>{t('registerDisplay.showRunningBalance')}</span>
            <Toggle checked={settings.showRunningBalance}
              onChange={(v) => void saveSetting('showRunningBalance', v)} />
          </div>

          <div className={rowCls}>
            <span className={rowLabelCls}>{t('registerDisplay.showReconcileColumn')}</span>
            <Toggle checked={settings.showReconcileColumn}
              onChange={(v) => void saveSetting('showReconcileColumn', v)} />
          </div>

          <div className={rowCls}>
            <span className={rowLabelCls}>{t('registerDisplay.defaultSortOrder')}</span>
            <select className={selectCls} value={settings.defaultSortOrder}
              onChange={(e) => void saveSetting('defaultSortOrder', e.target.value as 'date-asc' | 'date-desc')}>
              <option value="date-asc">{t('registerDisplay.dateAsc')}</option>
              <option value="date-desc">{t('registerDisplay.dateDesc')}</option>
            </select>
          </div>

          <div className={rowCls}>
            <div>
              <span className={rowLabelCls}>{t('registerDisplay.journalMode')}</span>
              <p className="text-xs text-text-subtle mt-0.5">{t('registerDisplay.journalNote')}</p>
            </div>
            <Toggle checked={settings.transactionJournalMode}
              onChange={(v) => void saveSetting('transactionJournalMode', v)} />
          </div>
        </div>

        {/* ── Connected Services ── */}
        <div id="connected-services" className={sectionCls}>
          <h3 className="text-sm font-semibold text-text mb-4">{t('connectedServices')}</h3>

          {/* Coinbase */}
          <div className="border border-border p-4">
            <div className="flex items-start justify-between mb-2">
              <div>
                <p className="text-sm font-medium text-text">Coinbase</p>
                <p className="text-xs text-text-muted mt-0.5">
                  {t('coinbase.desc')}
                </p>
              </div>
              {connected && (
                <button onClick={handleDisconnectCoinbase} disabled={disconnectingCoinbase}
                  className="ml-4 px-3 py-1 border border-border text-sm text-text-muted hover:bg-bg-elevated disabled:opacity-50 transition-colors shrink-0">
                  {disconnectingCoinbase ? t('coinbase.disconnecting') : t('coinbase.disconnect')}
                </button>
              )}
            </div>

            {connected && coinbaseCredential ? (
              <div className="mt-3 space-y-2">
                <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
                  <span className="text-text-muted">{t('common:sync.status')}</span>
                  <span className="text-positive text-xs font-medium">{t('coinbase.connected')}</span>
                  <span className="text-text-muted">{t('coinbase.connected')}</span>
                  <span className="text-text text-xs">
                    {formatDate(coinbaseCredential.connectedAt)}
                  </span>
                  <span className="text-text-muted">{t('coinbase.apiKey')}</span>
                  <span className="text-text text-xs font-mono">{coinbaseCredential.apiKey}</span>
                  <span className="text-text-muted">{t('common:sync.lastSynced')}</span>
                  <span className="text-text text-xs">
                    {lastSyncedDisplay}
                    {coinbaseCredential.lastSyncTransactionCount > 0 &&
                      ` (${t('coinbaseTransactions', { count: coinbaseCredential.lastSyncTransactionCount })})`}
                  </span>
                </div>
                {coinbaseError && (
                  <div className="border border-negative text-negative px-3 py-2 text-xs">{coinbaseError}</div>
                )}
                <div className="flex gap-2 mt-3">
                  <button className={secondaryBtnCls} onClick={() => void navigate('/import')}>
                    {t('coinbase.syncNow')}
                  </button>
                </div>
                <p className="text-xs text-text-muted mt-3">
                  {t('coinbase.storageNote')}
                </p>
              </div>
            ) : (
              <form onSubmit={(e) => void handleSaveCoinbaseCredential(e)} className="mt-3 space-y-3">
                {coinbaseError && (
                  <div className="border border-negative text-negative px-3 py-2 text-xs">{coinbaseError}</div>
                )}
                <div>
                  <label className={labelCls}>{t('coinbase.apiKeyName')}</label>
                  <input
                    type="text"
                    value={apiKeyInput}
                    onChange={(e) => setApiKeyInput(e.target.value)}
                    className={inputCls}
                    placeholder="organizations/…/apiKeys/…"
                    autoComplete="off"
                    spellCheck={false}
                  />
                </div>
                <div>
                  <label className={labelCls}>{t('coinbase.apiSecret')}</label>
                  <textarea
                    value={apiSecretInput}
                    onChange={(e) => setApiSecretInput(e.target.value)}
                    className={`${inputCls} font-mono text-xs resize-y min-h-[80px]`}
                    placeholder={'-----BEGIN EC PRIVATE KEY-----\n…\n-----END EC PRIVATE KEY-----'}
                    autoComplete="new-password"
                    spellCheck={false}
                  />
                </div>
                <button type="submit" disabled={verifyingCoinbase} className={primaryBtnCls}>
                  {verifyingCoinbase ? t('coinbase.verifying') : t('coinbase.saveConnect')}
                </button>
                <p className="text-xs text-text-muted">
                  {t('coinbase.generatePre')}{' '}
                  <span className="font-mono text-text">coinbase.com → Settings → API</span>{t('coinbase.generatePost')}
                </p>
              </form>
            )}
          </div>
        </div>

        {/* ── Data ── */}
        <div className={sectionCls}>
          <h3 className="text-sm font-semibold text-text mb-4">{t('sections.data')}</h3>
          <div className="space-y-3">
            <div>
              <p className="text-sm text-text-muted mb-2">
                {t('data.importPre')}{' '}
                <code className="bg-bg-elevated px-1 font-mono">.gnucash</code> {t('data.fileSuffix')}
              </p>
              {importSuccess && (
                <div className="border border-positive text-positive px-3 py-2 text-sm mb-2">{importSuccess}</div>
              )}
              <button onClick={() => { setSection('import'); setImportSuccess(''); }} className={secondaryBtnCls}>
                {t('data.importButton')}
              </button>
            </div>
            <div>
              <p className="text-sm text-text-muted mb-2">
                {t('data.exportPre')}{' '}
                <code className="bg-bg-elevated px-1 font-mono">.gnucash</code> {t('data.fileSuffix')}
              </p>
              {exportError && (
                <div className="border border-negative text-negative px-3 py-2 text-sm mb-2">{exportError}</div>
              )}
              <button onClick={handleExport} disabled={exporting} className={secondaryBtnCls}>
                {exporting ? t('data.exporting') : t('data.exportButton')}
              </button>
            </div>
            <div>
              <p className="text-sm text-text-muted mb-2">
                {t('data.exchangeRatesDesc')}
              </p>
              <button onClick={() => navigate('/settings/exchange-rates')} className={secondaryBtnCls}>
                {t('data.manageExchangeRates')}
              </button>
            </div>

            {/* ── Automatic price sync (opt-in; requests go via the sync-server
                   price proxy so no third party ever sees the user's IP or
                   asset list — see lib/priceSync.ts) ── */}
            <div className="border-t border-border pt-4">
              <div className="flex items-center justify-between mb-1">
                <span className={rowLabelCls}>{t('priceSync.autoTitle')}</span>
                <Toggle checked={settings.autoPriceSync}
                  onChange={(v) => void saveSetting('autoPriceSync', v)} />
              </div>
              <p className="text-xs text-text-muted mb-1">{t('priceSync.autoDesc')}</p>
              <p className="text-xs text-text-subtle mb-3">{t('priceSync.providersNote')}</p>
              <div className="mb-3">
                <label className="block text-xs text-text-muted mb-1" htmlFor="av-key">
                  {t('priceSync.avKeyTitle')}
                </label>
                <input
                  id="av-key"
                  type="password"
                  autoComplete="off"
                  defaultValue={settings.alphaVantageKey ?? ''}
                  placeholder={t('priceSync.avKeyPlaceholder')}
                  onBlur={(e) => {
                    const v = e.target.value.trim();
                    if (v !== (settings.alphaVantageKey ?? '')) {
                      void saveSetting('alphaVantageKey', v === '' ? null : v);
                    }
                  }}
                  className="w-full max-w-sm bg-bg-elevated border border-border text-text text-sm px-3 py-2 focus:border-accent focus:outline-none"
                />
                <p className="text-xs text-text-subtle mt-1">{t('priceSync.avKeyDesc')}</p>
              </div>
              <button
                onClick={() => void priceSync.run()}
                disabled={priceSync.status.running}
                className={secondaryBtnCls}
              >
                {priceSync.status.running ? t('priceSync.refreshing') : t('priceSync.refreshNow')}
              </button>
              {priceSync.status.error && (
                <p className="text-xs text-negative mt-2">
                  {t('priceSync.failed', { message: priceSync.status.error })}
                </p>
              )}
              {!priceSync.status.error && priceSync.status.updated !== null && !priceSync.status.running && (
                <p className="text-xs text-text-muted mt-2">
                  {priceSync.status.updated === 0
                    ? t('priceSync.allUpToDate')
                    : t('priceSync.updatedCount', { count: priceSync.status.updated })}
                  {priceSync.status.unresolved.length > 0 && (
                    <> {t('priceSync.unresolved', { symbols: priceSync.status.unresolved.join(', ') })}</>
                  )}
                  {priceSync.status.needsApiKey.length > 0 && (
                    <> {t('priceSync.equityNeedsKey', { symbols: priceSync.status.needsApiKey.join(', ') })}</>
                  )}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* ── Security ── */}
        <div className={sectionCls}>
          <h3 className="text-sm font-semibold text-text mb-4">{t('sections.security')}</h3>
          <div className="space-y-3">
            <div>
              <p className="text-sm text-text mb-1">{t('security.recoveryPhraseHeading')}</p>
              <p className="text-xs text-text-muted mb-2">
                {t('security.recoveryDesc')}
              </p>

              {recoveryError && (
                <div className="border border-negative text-negative px-3 py-2 text-xs mb-2 max-w-md">{recoveryError}</div>
              )}

              {recoveryPhrase ? (
                <div className="space-y-2 max-w-md">
                  <div className="border border-warning bg-bg-elevated p-3">
                    <p className="text-xs text-warning font-medium mb-3">
                      {t('security.recoveryWarning')}
                    </p>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                      {recoveryPhrase.split(' ').map((word, i) => (
                        <div key={i} className="flex items-baseline gap-2 bg-bg border border-border px-2 py-1">
                          <span className="text-xs text-text-subtle w-5 text-right tabular-nums shrink-0">{i + 1}</span>
                          <span className="text-sm font-mono text-text break-all">{word}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <button onClick={resetRecovery} className={secondaryBtnCls}>{t('common:actions.hide')}</button>
                </div>
              ) : recoverySection === 'prompt' ? (
                <form onSubmit={handleRevealRecovery} className="space-y-2 max-w-xs">
                  <input
                    type="password"
                    value={recoveryPassword}
                    onChange={(e) => setRecoveryPassword(e.target.value)}
                    className={inputCls}
                    placeholder={t('security.accountPasswordPlaceholder')}
                    autoFocus
                    autoComplete="current-password"
                  />
                  <div className="flex gap-2">
                    <button type="submit" disabled={revealingRecovery} className={primaryBtnCls}>
                      {revealingRecovery ? t('security.verifying') : t('security.reveal')}
                    </button>
                    <button type="button" onClick={resetRecovery} className={secondaryBtnCls}>{t('common:actions.cancel')}</button>
                  </div>
                </form>
              ) : (
                <button onClick={() => { setRecoverySection('prompt'); setRecoveryError(''); }} className={secondaryBtnCls}>
                  {t('security.showRecoveryPhrase')}
                </button>
              )}
            </div>
            <div className="border-t border-border pt-3">
              <p className="text-sm text-text mb-1">{t('security.changePassword')}</p>
              <p className="text-xs text-text-muted mb-2">
                {t('security.changePasswordDesc')}
              </p>
              <button onClick={() => setSection('change-password')} className={secondaryBtnCls}>
                {t('security.changePassword')}
              </button>
            </div>
            <div className="border-t border-border pt-3">
              <p className="text-sm text-text mb-1">{t('security.rotateKey')}</p>
              <p className="text-xs text-text-muted mb-2">
                {t('security.rotateKeyShortDesc')}
              </p>
              <button onClick={() => setSection('rotate-key')} className={secondaryBtnCls}>
                {t('security.rotateKey')}
              </button>
            </div>
            <div className="border-t border-border pt-3">
              <p className="text-sm text-text-muted mb-1">
                {t('security.syncServerLabel')} <span className="font-mono text-text">{auth.syncUrl}</span>
              </p>
            </div>
          </div>
        </div>

        {/* ── About ── */}
        <div className={sectionCls}>
          <h3 className="text-sm font-semibold text-text mb-4">{t('sections.about')}</h3>
          <p className="text-sm text-text-muted mb-1">
            {t('about.version')}{' '}
            <span className="font-mono text-text" data-testid="app-version">{BUILD_STAMP}</span>
          </p>
          <p className="text-sm text-text-muted mb-3">{t('about.license')}</p>
          <div className="flex gap-6 text-sm">
            <a
              href="https://github.com/kylepjohnson/hushold"
              target="_blank"
              rel="noreferrer"
              className="text-accent hover:text-accent-hover"
            >
              {t('about.viewSource')}
            </a>
            <a
              href="https://github.com/kylepjohnson/hushold/blob/master/docs/SECURITY_AUDIT.md"
              target="_blank"
              rel="noreferrer"
              className="text-accent hover:text-accent-hover"
            >
              {t('about.securityAudit')}
            </a>
          </div>
        </div>

        {/* ── Danger Zone ── */}
        <div className="border-l-2 border-negative p-5" style={{ background: 'rgba(192,57,43,0.08)' }}>
          <h3 className="text-sm font-semibold text-negative mb-1">{t('dangerZone')}</h3>
          <p className="text-sm text-text-muted mb-4">
            {t('dangerZoneDesc')}
          </p>
          <button onClick={() => setSection('delete')} className="px-4 py-2 border border-negative text-negative text-sm hover:bg-negative/10 transition-colors">
            {t('security.deleteAccount')}
          </button>
        </div>

      </div>
    </div>
  );
}
